import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@qargo-ims/ui';
import { apiFetch } from '@/lib/api/client';
import { useApi } from '@/lib/api/useApi';
import { useUserContext } from '@/lib/auth/useUserContext';
import { isHrStaff } from '@/lib/auth/rbac';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusVariant } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { formatShortDate } from '@/lib/format';

interface Ticket {
  id: string;
  type: string;
  typeLabel: string;
  subject: string;
  body: string;
  status: 'open' | 'resolved';
  requester: { entraOid: string; displayName: string | null; email: string | null };
  subjectBarista: { id: string; username: string; displayName: string | null } | null;
  resolutionNotes: string | null;
  resolvedBy: { entraOid: string; displayName: string | null } | null;
  createdAt: string;
  resolvedAt: string | null;
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function HrTicketDetail() {
  const { id = '' } = useParams();
  const { groups } = useUserContext();
  const { data, loading, error, refetch } = useApi<{ ticket: Ticket }>(
    id ? `/hr-tickets/${id}` : null,
  );
  const ticket = data?.ticket;
  const canResolve = isHrStaff(groups);

  const [notes, setNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function resolve() {
    setResolving(true);
    setMsg(null);
    try {
      await apiFetch(`/hr-tickets/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolutionNotes: notes.trim() || null }),
      });
      setMsg(
        ticket?.type === 'warning_copy'
          ? 'Acknowledged — the requester has been notified.'
          : 'Resolved — the requester has been notified.',
      );
      setNotes('');
      refetch();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setResolving(false);
    }
  }

  if (loading || !ticket) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/hr"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden /> HR requests
        </Link>
        {error ? (
          <p className="text-sm text-destructive">Could not load ticket: {error}</p>
        ) : (
          <Skeleton className="h-64 w-full rounded-xl" />
        )}
      </div>
    );
  }

  // warning_copy tickets are "acknowledged" by HR rather than "resolved".
  const isWarningCopy = ticket.type === 'warning_copy';
  const actionWord = isWarningCopy ? 'Acknowledge' : 'Resolve';
  const doneWord = isWarningCopy ? 'Acknowledged' : 'Resolved';

  return (
    <div className="space-y-6">
      <Link
        to="/admin/hr"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden /> HR requests
      </Link>
      <PageHeader
        title={ticket.subject}
        description={`${ticket.typeLabel} · opened ${formatShortDate(ticket.createdAt)} by ${
          ticket.requester.displayName ?? ticket.requester.email ?? 'a manager'
        }`}
        actions={<Badge variant={statusVariant(ticket.status)}>{ticket.status}</Badge>}
      />

      <Card>
        <CardHeader>
          <CardTitle>Request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="whitespace-pre-wrap text-sm">{ticket.body}</p>
          {ticket.subjectBarista ? (
            <p className="text-sm text-muted-foreground">
              About:{' '}
              {canResolve ? (
                <Link
                  to={`/admin/users/${ticket.subjectBarista.id}`}
                  className="underline underline-offset-4 hover:text-primary"
                >
                  {ticket.subjectBarista.displayName ?? `@${ticket.subjectBarista.username}`}
                </Link>
              ) : (
                <span>
                  {ticket.subjectBarista.displayName ?? `@${ticket.subjectBarista.username}`}
                </span>
              )}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {ticket.status === 'resolved' ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-success" aria-hidden />
              <CardTitle>{isWarningCopy ? 'Acknowledgment' : 'Resolution'}</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {doneWord} {formatShortDate(ticket.resolvedAt)}
              {ticket.resolvedBy?.displayName ? ` by ${ticket.resolvedBy.displayName}` : ''}.
            </p>
          </CardHeader>
          <CardContent>
            {ticket.resolutionNotes ? (
              <p className="whitespace-pre-wrap text-sm">{ticket.resolutionNotes}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">No resolution notes.</p>
            )}
          </CardContent>
        </Card>
      ) : canResolve ? (
        <Card>
          <CardHeader>
            <CardTitle>{actionWord}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {isWarningCopy
                ? 'Acknowledge receipt of this write-up. The manager who filed it is emailed your notes.'
                : 'Mark this request complete. The original requester is emailed your notes.'}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              className={inputClass}
              rows={4}
              value={notes}
              placeholder={`${isWarningCopy ? 'Acknowledgment' : 'Resolution'} notes (sent to the requester)…`}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={resolve} disabled={resolving}>
                <CheckCircle2 className="size-4" aria-hidden />
                {resolving ? `${actionWord}…` : `${actionWord} & notify requester`}
              </Button>
              {msg ? (
                <span
                  className={
                    msg.startsWith(doneWord) ? 'text-sm text-success' : 'text-sm text-destructive'
                  }
                >
                  {msg}
                </span>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-muted-foreground">
          This request is open. HR will follow up and you’ll be emailed when it’s resolved.
        </p>
      )}
    </div>
  );
}
